// SPDX-License-Identifier: LicenseRef-Proprietary
import { TreeNode, TreeEdge, NodeType } from './validation';

export type LegacyNode = {
  id: string;
  type: NodeType | string;
  label: string;
  nodeType?: 'healthy' | 'infection' | 'hospitalization' | 'death' | 'recover' | 'survive' | 'other'; // Epidemiological outcome type
  cost_eur?: number;
  qaly?: number;
  notes?: string;
  p?: number;
  transition_cost?: number;
  transition_utility_loss?: number;
  children?: LegacyNode[];
};

/**
 * Normalizes legacy tree format, moving transition costs/utilities from child nodes to edges.
 * Edges are the canonical source for transition data.
 * 
 * @param root - Legacy tree root node
 * @returns Normalized tree with nodes and edges (edges contain all transition data)
 */
export function fromLegacy(root: LegacyNode): { nodes: Record<string, TreeNode>; edges: TreeEdge[] } {
  const nodes: Record<string, TreeNode> = {};
  const edges: TreeEdge[] = [];

  const toNew = (n: LegacyNode): TreeNode => ({
    id: n.id,
    type: n.type as NodeType,
    label: n.label,
    nodeType: n.nodeType, // Preserve nodeType
    cost: n.cost_eur,
    qaly: n.qaly,
    notes: n.notes,
    children: n.children?.map(c => c.id) ?? []
  });

  const walk = (n: LegacyNode) => {
    nodes[n.id] = toNew(n);
    n.children?.forEach(c => {
      walk(c);
      const edge: TreeEdge = {
        parentId: n.id,
        childId: c.id,
      };
      
      // Add probability for chance nodes
      if (n.type === 'chance') {
        edge.probability = c.p ?? 0.5;
      }
      
      // Add transition cost if present
      if (c.transition_cost !== undefined) {
        edge.transition_cost = c.transition_cost;
      }
      
      // Add transition utility loss if present
      if (c.transition_utility_loss !== undefined) {
        edge.transition_utility_loss = c.transition_utility_loss;
      }
      
      edges.push(edge);
    });
  };

  walk(root);
  return { nodes, edges };
}
